<template>
  <div class="text-center avatar-input">
    <!--
      key forces vue-picture-input to remount when an existing Storage URL arrives,
      so prefill runs correctly on edit.
    -->
    <picture-input
      :key="pictureKey"
      ref="pictureInput"
      radius="50"
      :prefill="prefill"
      :prefill-options="prefillOptions"
      :width="size"
      :height="size"
      :size="maxFileSize"
      accept="image/*"
      :alert-on-error="false"
      hide-change-button
      @change="onPictureChange"
      @prefill="onPrefillOk"
      @error="onPictureError"
    />

    <div class="avatar-input__controls mt-2">
      <b-form-checkbox
        v-model="greyscaleOn"
        switch
        size="sm"
        class="d-inline-flex align-items-center justify-content-center"
        :disabled="processing || !hasImage"
        @change="onGreyscaleToggle"
      >
        Black &amp; white photo
      </b-form-checkbox>

      <p v-if="processing" class="avatar-input__status text-muted mb-0 mt-1">
        Optimising photo…
      </p>
      <p
        v-else-if="statusText"
        class="avatar-input__status text-muted mb-0 mt-1"
      >
        {{ statusText }}
      </p>
    </div>

    <h6 class="description mt-2 mb-0">{{ description }}</h6>
  </div>
</template>
<script>
import PictureInput from "vue-picture-input";
import i18n from "@/i18n";
import { processAvatarImage, formatBytes } from "@/helpers/imageProcess";

/** Longest edge after resize — sharp enough for avatars / booking UI */
const MAX_EDGE = 400;
/** JPEG quality after resize (good balance for faces) */
const JPEG_QUALITY = 0.8;

const DEFAULT_AVATAR = require("@/assets/img/default-avatar.png");

export default {
  components: {
    PictureInput
  },
  props: {
    value: String,
    /**
     * Persisted B&W preference (employee.imageGreyscale).
     * Use :greyscale.sync so the parent form can save/restore the toggle.
     */
    greyscale: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "200"
    },
    /**
     * Max *source* file size (MB) for the file picker.
     * Large camera uploads are accepted then compressed client-side.
     */
    maxFileSize: {
      type: String,
      default: "12"
    },
    description: {
      type: String,
      default: i18n.t("message.choosePicture")
    }
  },
  data() {
    return {
      /** Local toggle; initialised from prop so edit form restores saved B&W */
      greyscaleOn: !!this.greyscale,
      processing: false,
      /** Colour master (data URL or remote URL) used when re-applying B&W */
      masterSource: null,
      lastByteLength: null,
      /** Bumps when remote prefill URL changes so PictureInput remounts */
      pictureKey: "default"
    };
  },
  computed: {
    prefill() {
      // Prefer the current bound value (existing Storage URL or processed data URL)
      if (this.value && this.value !== DEFAULT_AVATAR) {
        return this.value;
      }
      return DEFAULT_AVATAR;
    },
    /**
     * Firebase Storage download URLs have no file extension in the path.
     * vue-picture-input derives MIME from the URL filename unless we force it.
     */
    prefillOptions() {
      if (this.value && String(this.value).startsWith("http")) {
        return {
          fileName: "employee-photo.jpg",
          mediaType: "image/jpeg"
        };
      }
      return {};
    },
    hasImage() {
      return !!(
        this.masterSource ||
        (this.value && this.value.startsWith("http")) ||
        (this.value && this.value.startsWith("data:"))
      );
    },
    statusText() {
      if (this.lastByteLength == null) {
        return this.hasImage
          ? "Photos are resized automatically before upload"
          : "";
      }
      const bw = this.greyscaleOn ? " · B&W" : "";
      return `Optimised to ${formatBytes(
        this.lastByteLength
      )} · max ${MAX_EDGE}px${bw}`;
    }
  },
  watch: {
    greyscale: {
      immediate: true,
      handler(next) {
        // Restore toggle from saved employee.imageGreyscale without re-processing
        this.greyscaleOn = !!next;
      }
    },
    value: {
      immediate: true,
      handler(next, prev) {
        // When edit form loads an existing Storage URL, remount the picker
        // so it prefills. Do not remount on our own processed data: URLs.
        if (!next || next === prev) return;
        if (next.startsWith("http")) {
          this.masterSource = next;
          this.pictureKey = next;
        }
      }
    }
  },
  methods: {
    setGreyscale(on) {
      this.greyscaleOn = !!on;
      this.$emit("update:greyscale", this.greyscaleOn);
    },
    onPrefillOk() {
      // Existing photo drawn successfully — keep master as remote URL for B&W
      if (this.value && this.value.startsWith("http") && !this.masterSource) {
        this.masterSource = this.value;
      }
    },
    async onPictureChange(base64String) {
      if (!base64String) {
        this.masterSource = null;
        this.lastByteLength = null;
        this.setGreyscale(false);
        this.$emit("input", null);
        return;
      }
      // Colour master from the cropper — B&W always re-derived from this
      this.masterSource = base64String;
      await this.emitProcessed();
    },
    async onGreyscaleToggle() {
      if (!this.masterSource && this.value) {
        this.masterSource = this.value;
      }
      if (!this.masterSource) {
        this.setGreyscale(false);
        return;
      }
      // Persist preference immediately so Save keeps the toggle even if user
      // does not re-crop; image pixels updated via emitProcessed below.
      this.$emit("update:greyscale", this.greyscaleOn);
      try {
        await this.emitProcessed();
      } catch (e) {
        this.setGreyscale(!this.greyscaleOn);
        this.$emit("error", {
          message:
            (e && e.message) ||
            "Could not update this photo. Try uploading it again."
        });
      }
    },
    async emitProcessed() {
      const source = this.masterSource;
      if (!source) return;

      this.processing = true;
      try {
        const result = await processAvatarImage(source, {
          maxEdge: MAX_EDGE,
          quality: JPEG_QUALITY,
          greyscale: this.greyscaleOn
        });
        this.lastByteLength = result.byteLength;
        this.$emit("input", result.dataUrl);
      } finally {
        this.processing = false;
      }
    },
    onPictureError(e) {
      // Prefill failures used to surface as blank avatars — show a clear message
      const message =
        (e && e.message) ||
        "Could not load photo. You can still upload a new one.";
      this.$emit("error", { message, type: e && e.type });
    },
    onError(e) {
      this.$emit("error", e);
    }
  }
};
</script>
<style scoped>
.avatar-input__controls {
  max-width: 260px;
  margin-left: auto;
  margin-right: auto;
}
.avatar-input__status {
  font-size: 12px;
  line-height: 1.35;
}
</style>
